import { useMemo } from 'react';

import { LearningProgressStatus } from 'src/services/http/progress';

import { CONTENT_TYPE_VALUES, CONTENT_TYPE_LABELS, STATUS_LABELS, DEFAULT_PRODUCT } from './constants';
import { FlattenedItem } from './useMyLearningData';

// Duration filter constants
const DURATION_FILTERS = {
  LESS_THAN_30_MIN: {
    label: 'Less than 30 min',
    matches: (duration: number) => duration < 30,
  },
  THIRTY_MIN_TO_ONE_HOUR: {
    label: '30 min to 1 hour',
    matches: (duration: number) => duration >= 30 && duration <= 60,
  },
  ONE_TO_TWO_HOURS: {
    label: '1 to 2 hours',
    matches: (duration: number) => duration > 60 && duration <= 120,
  },
  MORE_THAN_TWO_HOURS: {
    label: 'More than 2 hours',
    matches: (duration: number) => duration > 120,
  },
} as const;

interface FilterOptions {
  selectedStatus: string[];
  selectedType: string[];
  selectedApps: string[];
  selectedDuration: string[];
  currentSort: string;
}

export const useFilteredAndSortedItems = (
  allItems: FlattenedItem[],
  { selectedStatus, selectedType, selectedApps, selectedDuration, currentSort }: FilterOptions,
): FlattenedItem[] => {
  return useMemo((): FlattenedItem[] => {
    let items = [...allItems];

    // Apply type filter
    if (selectedType.length > 0) {
      items = items.filter((item) => {
        if (selectedType.includes(CONTENT_TYPE_LABELS.LESSON) && item.contentType === CONTENT_TYPE_VALUES.LESSON) {
          return true;
        }
        if (selectedType.includes(CONTENT_TYPE_LABELS.COURSE) && item.contentType === CONTENT_TYPE_VALUES.COURSE) {
          return true;
        }
        if (
          selectedType.includes(CONTENT_TYPE_LABELS.LEARNING_PATH) &&
          item.contentType === CONTENT_TYPE_VALUES.LEARNING_PATH
        ) {
          return true;
        }

        return false;
      });
    }

    // Apply status filter
    if (selectedStatus.length > 0) {
      items = items.filter((item) => {
        const status = item.status;

        if (selectedStatus.includes(STATUS_LABELS.NOT_STARTED) && status === LearningProgressStatus.NOT_STARTED) {
          return true;
        }
        if (selectedStatus.includes(STATUS_LABELS.IN_PROGRESS) && status === LearningProgressStatus.IN_PROGRESS) {
          return true;
        }
        if (selectedStatus.includes(STATUS_LABELS.COMPLETE) && status === LearningProgressStatus.COMPLETED) {
          return true;
        }
        if (selectedStatus.includes(STATUS_LABELS.SAVED) && item.isSaved) {
          return true;
        }
        if (selectedStatus.includes(STATUS_LABELS.RETIRED) && item.isRetired) {
          return true;
        }

        return false;
      });
    }

    // Apply apps/product filter
    if (selectedApps.length > 0) {
      items = items.filter((item) => {
        // If productsList exists, check against all products in the array
        if (item.productsList && item.productsList.length > 0) {
          return selectedApps.some((app) =>
            item.productsList!.some((prod) => prod.toLowerCase().includes(app.toLowerCase())),
          );
        }

        // Fallback to single product string
        const product = item.product || DEFAULT_PRODUCT;

        return selectedApps.some((app) => product.toLowerCase().includes(app.toLowerCase()));
      });
    }

    // Apply duration filter
    if (selectedDuration.length > 0) {
      items = items.filter((item) => {
        const duration = item.duration || 0;

        return selectedDuration.some((filter) => {
          const durationFilter = Object.values(DURATION_FILTERS).find((df) => df.label === filter);

          return durationFilter?.matches(duration) || false;
        });
      });
    }

    // Apply sorting
    items.sort((a, b) => {
      if (currentSort === 'More recent') {
        const dateA = new Date(a.startedDate || a.lastVisitedDate || 0).getTime();
        const dateB = new Date(b.startedDate || b.lastVisitedDate || 0).getTime();

        return dateB - dateA;
      } else if (currentSort === 'Least recent') {
        const dateA = new Date(a.startedDate || a.lastVisitedDate || 0).getTime();
        const dateB = new Date(b.startedDate || b.lastVisitedDate || 0).getTime();

        return dateA - dateB;
      } else if (currentSort === 'Most % progress') {
        const progressA = a.subCollectionTotal > 0 ? a.progressTotal / a.subCollectionTotal : 0;
        const progressB = b.subCollectionTotal > 0 ? b.progressTotal / b.subCollectionTotal : 0;

        return progressB - progressA;
      } else if (currentSort === 'Least % progress') {
        const progressA = a.subCollectionTotal > 0 ? a.progressTotal / a.subCollectionTotal : 0;
        const progressB = b.subCollectionTotal > 0 ? b.progressTotal / b.subCollectionTotal : 0;

        return progressA - progressB;
      }

      return 0;
    });

    return items;
  }, [allItems, selectedType, selectedStatus, selectedApps, selectedDuration, currentSort]);
};
